import React, { useState } from 'react';
import { db } from '../services/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { businessService } from '../services/businessService';
import { userService } from '../services/userService';
import { messageService } from '../services/messageService';

const FirebaseTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [conversations, setConversations] = useState<any[]>([]);

  // Test Firebase connection
  const testConnection = async () => {
    setLoading(true);
    setMessage('Testing Firebase connection...');
    
    try {
      const querySnapshot = await getDocs(collection(db, 'categories'));
      setMessage(`✅ Firebase connected! Found ${querySnapshot.size} categories`);
    } catch (error) {
      setMessage(`❌ Firebase connection failed: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  // Load businesses from Firestore
  const loadBusinesses = async () => {
    setLoading(true);
    setMessage('Loading businesses from Firestore...');
    
    try {
      const result = await businessService.getBusinesses();
      setBusinesses(result.businesses);
      setMessage(`✅ Loaded ${result.businesses.length} businesses from Firestore`);
    } catch (error) {
      setMessage(`❌ Failed to load businesses: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  // Load users from Firestore
  const loadUsers = async () => {
    setLoading(true);
    setMessage('Loading users from Firestore...');
    
    try {
      const result = await userService.getUsers();
      setUsers(result);
      setMessage(`✅ Loaded ${result.length} users from Firestore`);
    } catch (error) {
      setMessage(`❌ Failed to load users: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  // Load conversations from Firestore
  const loadConversations = async () => {
    setLoading(true);
    setMessage('Loading conversations from Firestore...');
    
    try {
      if (users.length > 0) {
        const result = await messageService.getConversationsForUser(users[0].id || '');
        setConversations(result);
        setMessage(`✅ Loaded ${result.length} conversations from Firestore`);
      } else {
        setMessage('⚠️ No users found. Please load users first.');
      }
    } catch (error) {
      setMessage(`❌ Failed to load conversations: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Firebase Test Panel</h1>
      
      {/* Connection Test */}
      <div className="bg-blue-50 p-4 rounded-lg mb-6">
        <h2 className="text-xl font-semibold mb-4">Connection Test</h2>
        <button
          onClick={testConnection}
          disabled={loading}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
        >
          Test Firebase Connection
        </button>
      </div>

      {/* Data Loading */}
      <div className="bg-purple-50 p-4 rounded-lg mb-6">
        <h2 className="text-xl font-semibold mb-4">Data Loading</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={loadBusinesses}
            disabled={loading}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            Load Businesses
          </button>
          <button
            onClick={loadUsers}
            disabled={loading}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            Load Users
          </button>
          <button
            onClick={loadConversations}
            disabled={loading}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            Load Conversations
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {message && (
        <div className={`p-4 rounded mb-6 ${
          message.includes('✅') ? 'bg-green-100 text-green-800' : 
          message.includes('❌') ? 'bg-red-100 text-red-800' : 
          'bg-yellow-100 text-yellow-800'
        }`}>
          {message}
        </div>
      )}

      {/* Data Display */}
      {businesses.length > 0 && (
        <div className="bg-white p-4 rounded-lg mb-6">
          <h2 className="text-xl font-semibold mb-4">Businesses ({businesses.length})</h2>
          <div className="space-y-2">
            {businesses.slice(0, 5).map((business) => (
              <div key={business.id} className="p-2 bg-gray-50 rounded">
                <strong>{business.basic.title}</strong> - {business.location.city}, {business.location.province}
              </div>
            ))}
            {businesses.length > 5 && (
              <div className="text-gray-500 text-sm">... and {businesses.length - 5} more</div>
            )}
          </div>
        </div>
      )}

      {users.length > 0 && (
        <div className="bg-white p-4 rounded-lg mb-6">
          <h2 className="text-xl font-semibold mb-4">Users ({users.length})</h2>
          <div className="space-y-2">
            {users.slice(0, 5).map((user) => (
              <div key={user.id} className="p-2 bg-gray-50 rounded">
                <strong>{user.profile.firstName} {user.profile.lastName}</strong> - {user.profile.userType}
              </div>
            ))}
            {users.length > 5 && (
              <div className="text-gray-500 text-sm">... and {users.length - 5} more</div>
            )}
          </div>
        </div>
      )}

      {conversations.length > 0 && (
        <div className="bg-white p-4 rounded-lg mb-6">
          <h2 className="text-xl font-semibold mb-4">Conversations ({conversations.length})</h2>
          <div className="space-y-2">
            {conversations.slice(0, 5).map((conversation) => (
              <div key={conversation.id} className="p-2 bg-gray-50 rounded">
                <strong>Conversation {conversation.id}</strong> - {conversation.participants.length} participants
              </div>
            ))}
            {conversations.length > 5 && (
              <div className="text-gray-500 text-sm">... and {conversations.length - 5} more</div>
            )}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Instructions</h2>
        <div className="space-y-2 text-sm">
          <p>1. <strong>Test Connection:</strong> Verify Firebase is connected</p>
          <p>2. <strong>Load Data:</strong> Load existing data from Firestore</p>
          <p>3. <strong>View Data:</strong> See the loaded data below</p>
          <p className="text-gray-600 mt-4">
            <strong>Note:</strong> This panel is for testing Firestore connectivity. 
            All business data is now stored in Firestore and loaded dynamically.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FirebaseTest; 